import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  TextField,
  Typography,
  Snackbar,
  Box,
  Container,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Email, Lock, Visibility, VisibilityOff } from "@mui/icons-material";
import chaletApi from "api/chaletApi";
import { Alert } from "components/common";
import { handleApiError } from "utils";
import useUser, { ChaletRoles } from "hooks/useUser";
import { ReactComponent as ChaletLogo } from "assets/ic_chalet.svg";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [login, { isLoading, error }] = chaletApi.useLoginMutation();
  const [open, setOpen] = React.useState(false);
  const userHook = useUser();

  useEffect(() => {
    const { sectorId, isLoggedIn, role } = userHook;
    if (isLoggedIn) {
      if (role === ChaletRoles.admin) {
        navigate("/admin/");
      } else if (role === ChaletRoles.sectorAdmin) {
        navigate(`/admin/sectors/${sectorId}/activeOrders`);
      } else if (role === ChaletRoles.customerService) {
        navigate(`/admin/sectors/${sectorId}/activeOrders`);
      }
    }
  }, [userHook]);

  const handleLoginClick = async () => {
    try {
      await login({ email, password }).unwrap();
    } catch (error) {
      setOpen(true);
      console.log("Error", error);
    }
  };

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const errorMessage = useMemo(() => {
    return handleApiError(error);
  }, [error]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: (theme) =>
          `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 200%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <Box sx={{ p: 4 }}>
            {/* Logo Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 4,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,

                  boxShadow: (theme) => `0 8px 32px ${theme.palette.primary.main}33`,
                }}
              >
                <ChaletLogo
                  style={{
                    width: 48,
                    height: 48,
                    color: "white",
                    transform: "translateX(3px)",
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: "#2c3e50",
                  mb: 1,
                }}
              >
                Chalet Admin
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#7f8c8d",
                  textAlign: "center",
                  mb: 3,
                }}
              >
                Sign in to your admin dashboard
              </Typography>
            </Box>

            {/* Login Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleLoginClick();
              }}
            >
              <TextField
                fullWidth
                label="Email Address"
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: "#7f8c8d" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255, 255, 255, 1)",
                    },
                  },
                }}
              />
              <TextField
                fullWidth
                label="Password"
                variant="outlined"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: "#7f8c8d" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: "#7f8c8d" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 4,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255, 255, 255, 1)",
                    },
                  },
                }}
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading || !email || !password}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  fontWeight: 600,
                  fontSize: "1.1rem",
                  textTransform: "none",
                  boxShadow: (theme) => `0 8px 32px ${theme.palette.primary.main}33`,
                  "&:hover": {
                    background: (theme) =>
                      `linear-gradient(135deg, ${theme.palette.primary.main}dd 0%, ${theme.palette.secondary.main}dd 100%)`,
                    boxShadow: (theme) => `0 12px 40px ${theme.palette.primary.main}44`,
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": {
                    background: "#cbd5e0",
                    color: "#a0aec0",
                    boxShadow: "none",
                    transform: "none",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {isLoading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </Box>
        </Paper>
      </Container>
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
